import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Checkbox,
  Label,
  Button
} from '@bliks-io/ui';
import {
  slugify,
  type ExportProcessQuery,
  type ProcessStepRecordingDisplay,
  type CapacityBinding
} from '@bliks-io/contracts';
import { useExportProcess } from '@/api/queries/useProcesses';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { useAssigneeLookup } from '@/lib/useAssigneeLookup';
import { downloadBlob } from '@/lib/file-utils';
import { type ProcessStepsSheetLabels } from './processStepsExcel';
import { formatFrequency } from '@/lib/formatFrequency';
import { type ProcessEdgesSheetLabels } from './processEdges';
import { downloadProcessExcel } from './processExcelWorkbook';

interface ProcessExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  processId: string;
  processName: string;
  // The already-loaded, populated steps + per-step capacity drive the
  // client-side Excel export; they are exactly what `ProcessStepsTable` renders.
  steps: ProcessStepRecordingDisplay[];
  stepCapacities?: Record<string, CapacityBinding>;
}

// The server-side JSON/YAML export's section toggles — every query field except
// `format`, which has its own control. Derived from the contract so it can't drift.
type SectionFlags = Omit<ExportProcessQuery, 'format'>;
type ExportToggleKey = keyof SectionFlags;

// Excel is built in the browser, so it is a client-only format that sits beside
// the contract's server-side `format` enum rather than extending it.
type ExportFormat = ExportProcessQuery['format'] | 'xlsx';

const EXPORT_SECTIONS: ReadonlyArray<{ key: ExportToggleKey; labelKey: string }> = [
  { key: 'includeSteps', labelKey: 'processDetail.export.steps' },
  { key: 'includeAssignments', labelKey: 'processDetail.export.assignments' },
  { key: 'includeCapacity', labelKey: 'processDetail.export.capacity' },
  { key: 'includeContext', labelKey: 'processDetail.export.context' }
];

const FORMAT_OPTIONS: ReadonlyArray<{ value: ExportFormat; labelKey: string }> = [
  { value: 'json', labelKey: 'processDetail.export.formatJson' },
  { value: 'yaml', labelKey: 'processDetail.export.formatYaml' },
  { value: 'xlsx', labelKey: 'processDetail.export.formatExcel' }
];

const DEFAULT_SECTIONS: SectionFlags = {
  includeSteps: true,
  includeAssignments: true,
  includeCapacity: true,
  includeContext: false
};

export function ProcessExportDialog({
  open,
  onOpenChange,
  processId,
  processName,
  steps,
  stepCapacities
}: ProcessExportDialogProps) {
  const { t } = useTranslation();
  const exportProcess = useExportProcess(processId);
  const lookup = useAssigneeLookup();
  const [sections, setSections] = useState<SectionFlags>(DEFAULT_SECTIONS);
  const [format, setFormat] = useState<ExportFormat>('json');
  const [includeMetrics, setIncludeMetrics] = useState(true);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const isExcel = format === 'xlsx';

  const toggle = (key: ExportToggleKey) => (checked: boolean | 'indeterminate') =>
    setSections((prev) => ({ ...prev, [key]: checked === true }));

  const sheetLabels = (): ProcessStepsSheetLabels => ({
    stepNumber: t('processes.stepNumber'),
    type: t('processes.type'),
    step: t('processes.processStep'),
    stepDescription: t('processes.stepDescription'),
    inputs: t('processes.inputs'),
    assignedTo: t('processes.assignedTo'),
    recordedBy: t('processFlow.recordedBy'),
    whoElse: t('processPage.whoElseDoesThis'),
    helperSystems: t('processes.helperSystems'),
    helperPersons: t('processes.helperPersons'),
    helperOrgFunctions: t('processes.helperOrgFunctions'),
    helperDocuments: t('processes.helperDocuments'),
    lastChange: t('processes.lastChange'),
    rating: t('processes.rating'),
    frequency: t('processes.frequencyLabel'),
    duration: t('processes.duration'),
    capacity: t('processes.capacityBinding'),
    capacitySource: t('processDetail.export.capacitySource'),
    capacitySourceSurvey: t('processDetail.export.capacitySourceSurvey'),
    capacitySourceManual: t('processDetail.export.capacitySourceManual'),
    taskType: t('processes.task'),
    decisionType: t('processes.decision')
  });

  const edgeLabels = (): ProcessEdgesSheetLabels => ({
    stepNumber: t('processes.stepNumber'),
    processStep: t('processes.processStep'),
    conditions: t('processes.conditions'),
    output: t('processes.output'),
    successorNumber: t('processes.successorNumber'),
    successor: t('processes.successor')
  });

  const downloadExcel = async () => {
    setIsExportingExcel(true);
    try {
      await downloadProcessExcel({
        steps,
        stepCapacities,
        lookup,
        labels: sheetLabels(),
        edgeLabels: edgeLabels(),
        formatFrequency: (value, timesPerPeriod) => formatFrequency(value, timesPerPeriod, t),
        includeMetrics,
        processName,
        stepsSheetName: t('processes.stepsSheet'),
        edgesSheetName: t('processes.branches')
      });
      toast.success(t('processDetail.export.success'));
      onOpenChange(false);
    } catch {
      toast.error(t('processDetail.export.error'));
    } finally {
      setIsExportingExcel(false);
    }
  };

  const downloadServerExport = async (serverFormat: ExportProcessQuery['format']) => {
    try {
      const blob = await exportProcess.mutateAsync({ ...sections, format: serverFormat });
      const filename = `process-${slugify(processName) || 'process'}.${serverFormat}`;
      downloadBlob(blob, filename);

      toast.success(t('processDetail.export.success'));
      onOpenChange(false);
    } catch (error) {
      toastApiError(error, 'processDetail.export.error');
    }
  };

  const handleDownload = () => (format === 'xlsx' ? downloadExcel() : downloadServerExport(format));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('processDetail.export.title')}</DialogTitle>
          <DialogDescription>{t('processDetail.export.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="font-normal">{t('processDetail.export.format')}</Label>
            <div className="flex gap-2">
              {FORMAT_OPTIONS.map(({ value, labelKey }) => (
                <Button
                  key={value}
                  size="small"
                  variant={format === value ? 'primary' : 'outline'}
                  onClick={() => setFormat(value)}
                >
                  {t(labelKey)}
                </Button>
              ))}
            </div>
          </div>

          {/* Excel always exports the steps table; its only option is whether to
              add the metric columns. The server-side toggles don't apply. */}
          {isExcel ? (
            <div className="flex items-center gap-2">
              <Checkbox
                id="export-includeMetrics"
                checked={includeMetrics}
                onCheckedChange={(checked) => setIncludeMetrics(checked === true)}
              />
              <Label htmlFor="export-includeMetrics" className="font-normal cursor-pointer">
                {t('processDetail.export.includeMetrics')}
              </Label>
            </div>
          ) : (
            EXPORT_SECTIONS.map(({ key, labelKey }) => (
              <div key={key} className="flex items-center gap-2">
                <Checkbox
                  id={`export-${key}`}
                  checked={sections[key]}
                  onCheckedChange={toggle(key)}
                />
                <Label htmlFor={`export-${key}`} className="font-normal cursor-pointer">
                  {t(labelKey)}
                </Label>
              </div>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('processDetail.export.cancel')}
          </Button>
          <Button onClick={handleDownload} loading={exportProcess.isPending || isExportingExcel}>
            {t('processDetail.export.download')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
